import { useEffect, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { usePortalAnnouncement } from '@hooks/api/usePortalAnnouncementApi';
import { LocalStorageKeyConstants } from '@constants/general/localStorageKeyConstants';

// The banner is fixed above the fixed AppBar, so nothing below it can be laid
// out until its height is known. Publish that height as a CSS variable and let
// NavigationMenu, PageLayout and MobileMenu offset themselves by it. The
// variable is absent when no banner is showing, so the 0px fallback applies.
const OFFSET_VARIABLE = '--announcement-offset';

// Match Holders portal styling: centered text, no info icon, button blue.
const BANNER_BACKGROUND = 'rgba(0, 70, 204, 1)';

// ponytail: dismissal stores the message text, not a boolean, so a newly
// published announcement still shows after an earlier one was dismissed.
// Cleared on logout (AppContext) so the banner returns on the next login.
const AnnouncementBanner = () => {
  const { data } = usePortalAnnouncement();
  const message = data?.message ?? null;

  const [dismissedMessage, setDismissedMessage] = useState(() =>
    sessionStorage.getItem(LocalStorageKeyConstants.ANNOUNCEMENT_DISMISSED)
  );
  const [bannerElement, setBannerElement] = useState<HTMLDivElement | null>(
    null
  );

  useEffect(() => {
    const root = document.documentElement;

    if (!bannerElement) {
      root.style.removeProperty(OFFSET_VARIABLE);
      return;
    }

    // Long messages wrap to a different height per breakpoint, so measure
    // rather than assuming a fixed banner height.
    const observer = new ResizeObserver(() => {
      root.style.setProperty(
        OFFSET_VARIABLE,
        `${bannerElement.offsetHeight}px`
      );
    });
    observer.observe(bannerElement);

    return () => {
      observer.disconnect();
      root.style.removeProperty(OFFSET_VARIABLE);
    };
  }, [bannerElement]);

  const handleDismiss = () => {
    if (!message) {
      return;
    }

    sessionStorage.setItem(
      LocalStorageKeyConstants.ANNOUNCEMENT_DISMISSED,
      message
    );
    setDismissedMessage(message);
  };

  if (!message || dismissedMessage === message) {
    return null;
  }

  return (
    <Box
      ref={setBannerElement}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        // Above the AppBar, but below modals so dialogs still cover it.
        zIndex: theme => theme.zIndex.appBar + 1,
      }}
    >
      <Alert
        id="PortalAnnouncementBanner"
        severity="info"
        variant="filled"
        icon={false}
        onClose={handleDismiss}
        sx={{
          borderRadius: 0,
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'center',
          // Room for the absolute close button (same as Holders).
          py: '18px',
          px: '44px',
          backgroundColor: BANNER_BACKGROUND,
          color: '#ffffff',
          fontFamily: 'Epilogue, Arial, sans-serif',
          fontSize: { xs: '14px', sm: '16px' },
          lineHeight: 1.45,
          '& .MuiAlert-message': {
            overflowWrap: 'anywhere',
            py: 0,
            width: '100%',
            textAlign: 'center',
          },
          '& .MuiAlert-action': {
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            padding: 0,
            marginRight: 0,
          },
          '& .MuiIconButton-root': {
            color: '#ffffff',
          },
        }}
      >
        {message}
      </Alert>
    </Box>
  );
};

export default AnnouncementBanner;
